import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, FileText, Loader2, Plus, Trash2, X } from "lucide-react";
import { API, formatApiError } from "../auth/api";

/**
 * 2026-10-25: Overenskomsternes satser (fx ATL) er data, som ContainerFlow
 * vedligeholder her - med dato. Før stod de i koden og ville stå stille, når der
 * kom nye satser 1. marts. Firmaerne får besked i Løn, når der er nyere satser
 * end deres egne; deres satser ændres først, når de selv trykker "Tilføj".
 */
const RATE_FIELDS = [["ot1", "Overtid 1"], ["ot2", "Overtid 2"], ["aften", "Aften"], ["nat", "Nat"]];
const dk = (v) => String(v ?? "").replace(".", ",");
const num = (v) => String(v ?? "").replace(",", ".");
const input = "px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40";
const label = "block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1";
const nextMarch = (vf) => { const y = Number((vf || "").slice(0, 4)) || new Date().getFullYear(); return `${y + 1}-03-01`; };
const blank = () => ({ id: "", short: "", name: "", source: "", step_months: 0, versions: [{ valid_from: `${new Date().getFullYear()}-03-01`, base_rate: "", step_rate: "", rates: {}, pct1: [], pct2: [] }] });

export default function SuperAdminAgreements() {
  const [list, setList] = useState(null);
  const [edit, setEdit] = useState(null);      // kopi af den overenskomst, der rettes
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { setList((await axios.get(`${API}/superadmin/pay-agreements`)).data || []); }
    catch (e) { toast.error(formatApiError(e)); setList([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setV = (i, patch) => setEdit((a) => ({ ...a, versions: a.versions.map((v, j) => (j === i ? { ...v, ...patch } : v)) }));
  const save = async () => {
    if (!edit.name.trim()) { toast.error("Skriv overenskomstens navn"); return; }
    const id = edit.id || edit.name;
    setSaving(true);
    try {
      await axios.put(`${API}/superadmin/pay-agreements/${encodeURIComponent(id)}`, edit);
      toast.success("Satserne er gemt - firmaerne får besked i Løn, hvis der er nyere satser end deres");
      setEdit(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Slet "${a.name}"? Firmaernes egne satser ændres ikke - de kan bare ikke hente nye herfra.`)) return;
    try { await axios.delete(`${API}/superadmin/pay-agreements/${a.id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section className="space-y-4" data-testid="agreements-panel">
      <div className="bg-gradient-to-r from-emerald-50 to-blue-50 dark:from-emerald-950/30 dark:to-blue-950/30 rounded-xl p-5 border border-emerald-200 dark:border-emerald-800">
        <h2 className="font-heading font-bold text-xl flex items-center gap-2 mb-2"><FileText className="w-6 h-6 text-emerald-600" /> Overenskomster</h2>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Satserne, som firmaerne kan hente under Løn › Regler › Lønsatser og kroner. Når der kommer nye satser (typisk 1. marts),
          så tilføj en ny række med datoen her. Firmaer, der har hentet satser fra overenskomsten, får besked i Løn - deres egne
          satser ændres først, når de selv trykker "Tilføj". Den forrige udgave gemmes, hver gang der gemmes.
        </p>
      </div>

      {!list ? <div className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div> : !edit && (
        <div className="space-y-2">
          {list.map((a) => {
            const last = a.versions[a.versions.length - 1];
            return (
              <div key={a.id} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 flex flex-wrap items-center gap-3" data-testid={`agreement-${a.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-bold">{a.name} <span className="text-xs font-semibold text-slate-500">({a.short})</span></div>
                  <div className="text-xs text-slate-500">
                    {a.versions.length} {a.versions.length === 1 ? "sæt satser" : "sæt satser"} · seneste fra {last ? new Date(last.valid_from + "T12:00:00").toLocaleDateString("da-DK") : "-"}
                    {last ? ` · grundløn ${Number(last.base_rate).toLocaleString("da-DK", { minimumFractionDigits: 2 })} kr/t` : ""} · opdateret {a.updated_at ? new Date(a.updated_at).toLocaleDateString("da-DK") : "-"}{a.updated_by ? ` af ${a.updated_by}` : ""}
                  </div>
                </div>
                <button className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 text-sm font-semibold" onClick={() => setEdit(JSON.parse(JSON.stringify(a)))} data-testid={`agreement-edit-${a.id}`}>Ret satser</button>
                <button className="text-red-700" onClick={() => remove(a)} aria-label={`Slet ${a.name}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            );
          })}
          <button className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 text-sm font-semibold inline-flex items-center gap-1" onClick={() => setEdit(blank())}><Plus className="w-4 h-4" /> Ny overenskomst</button>
        </div>
      )}

      {edit && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-4" data-testid="agreement-form">
          <div className="grid md:grid-cols-[1fr_120px_160px] gap-3">
            <div><span className={label}>Navn</span><input className={`${input} w-full`} value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="fx ATL Transport- og Logistikoverenskomsten - chauffør" /></div>
            <div><span className={label}>Kort navn</span><input className={`${input} w-full`} value={edit.short} onChange={(e) => setEdit({ ...edit, short: e.target.value })} placeholder="fx ATL" /></div>
            <div><span className={label}>Højere sats efter (mdr.)</span><input className={`${input} w-full`} value={edit.step_months || ""} placeholder="0 = intet trin" inputMode="numeric"
              onChange={(e) => setEdit({ ...edit, step_months: Number(e.target.value.replace(/\D/g, "")) || 0 })} /></div>
          </div>
          <div><span className={label}>Kilde (hvor satserne står)</span><input className={`${input} w-full`} value={edit.source} onChange={(e) => setEdit({ ...edit, source: e.target.value })} placeholder="fx overenskomstens § og årstal" /></div>

          <div className="space-y-3">
            {edit.versions.map((v, i) => (
              <div key={i} className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-2" data-testid={`agreement-version-${i}`}>
                <div className="flex flex-wrap items-end gap-2">
                  <div><span className={label}>Gælder fra</span><input type="date" className={input} value={v.valid_from} onChange={(e) => setV(i, { valid_from: e.target.value })} /></div>
                  <div><span className={label}>Grundløn kr/t</span><input className={`${input} w-24`} value={dk(v.base_rate)} onChange={(e) => setV(i, { base_rate: num(e.target.value) })} /></div>
                  {edit.step_months > 0 && <div><span className={label}>Efter {edit.step_months} mdr.</span><input className={`${input} w-24`} value={dk(v.step_rate)} onChange={(e) => setV(i, { step_rate: num(e.target.value) })} /></div>}
                  {RATE_FIELDS.map(([k, l]) => (
                    <div key={k}><span className={label}>{l} kr/t</span><input className={`${input} w-20`} value={dk((v.rates || {})[k])} onChange={(e) => setV(i, { rates: { ...(v.rates || {}), [k]: num(e.target.value) } })} /></div>
                  ))}
                  {edit.versions.length > 1 && <button className="pb-2 text-red-700" onClick={() => setEdit({ ...edit, versions: edit.versions.filter((_, j) => j !== i) })} aria-label="Fjern satserne"><X className="w-4 h-4" /></button>}
                </div>
                {[["pct1", "Procenter af timeløn med tillæg (fx frihedskonto)"], ["pct2", "Procenter af summen (fx feriepenge, pension)"]].map(([k, l]) => (
                  <div key={k}>
                    <span className={label}>{l}</span>
                    <div className="flex flex-wrap items-center gap-2">
                      {(v[k] || []).map((p, pi) => (
                        <span key={pi} className="inline-flex items-center gap-1">
                          <input className={`${input} w-40`} value={p.name} onChange={(e) => setV(i, { [k]: v[k].map((q, qi) => (qi === pi ? { ...q, name: e.target.value } : q)) })} />
                          <input className={`${input} w-16`} value={dk(p.pct)} onChange={(e) => setV(i, { [k]: v[k].map((q, qi) => (qi === pi ? { ...q, pct: num(e.target.value) } : q)) })} />%
                          <button onClick={() => setV(i, { [k]: v[k].filter((_, qi) => qi !== pi) })} aria-label="Fjern"><X className="w-3.5 h-3.5" /></button>
                        </span>
                      ))}
                      <button className="text-xs font-semibold text-emerald-700" onClick={() => setV(i, { [k]: [...(v[k] || []), { name: "", pct: "" }] })}>+ Tilføj</button>
                    </div>
                  </div>
                ))}
              </div>
            ))}
            <button className="text-sm font-semibold text-emerald-700 inline-flex items-center gap-1" data-testid="agreement-add-version"
              onClick={() => { const last = edit.versions[edit.versions.length - 1] || blank().versions[0]; setEdit({ ...edit, versions: [...edit.versions, { ...JSON.parse(JSON.stringify(last)), valid_from: nextMarch(last.valid_from) }] }); }}>
              <Plus className="w-4 h-4" /> Nye satser fra dato (kopierer de seneste)</button>
          </div>

          <div className="flex justify-end gap-2">
            <button className="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 text-sm font-semibold" onClick={() => setEdit(null)}>Annuller</button>
            <button className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-50" disabled={saving} onClick={save} data-testid="agreement-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Gem</button>
          </div>
        </div>
      )}
    </section>
  );
}
